/**
 * Global search - always visible in sidebar header.
 * Searches orders, products, customers, and quotes in real-time.
 */

import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router'
import { globalSearch } from '../../../lib/admin-api.js'
import type { SearchResult, SearchResultItem } from '@provendoro/shared/types'

const entityTypeLabel: Record<SearchResultItem['entityType'], string> = {
  order: 'Order',
  product: 'Product',
  customer: 'Customer',
  quote: 'Quote',
}

const entityTypeColor: Record<SearchResultItem['entityType'], string> = {
  order: 'var(--color-primary)',
  product: 'oklch(0.6 0.15 260)',
  customer: 'oklch(0.6 0.15 200)',
  quote: 'oklch(0.65 0.15 70)',
}

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(id)
  }, [value, delay])
  return debounced
}

export function GlobalSearch() {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult | null>(null)
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const debouncedQuery = useDebounce(query, 220)

  useEffect(() => {
    if (debouncedQuery.trim().length < 2) {
      setResults(null)
      setOpen(false)
      return
    }
    let cancelled = false
    setLoading(true)
    globalSearch(debouncedQuery).then((res) => {
      if (cancelled) return
      setResults(res)
      setOpen(true)
      setActiveIndex(-1)
      setLoading(false)
    }).catch(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [debouncedQuery])

  // Outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const allItems: SearchResultItem[] = results
    ? [...results.orders, ...results.products, ...results.customers, ...results.quotes]
    : []

  const handleSelect = useCallback((item: SearchResultItem) => {
    setQuery('')
    setResults(null)
    setOpen(false)
    navigate(item.url)
  }, [navigate])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || allItems.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, allItems.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault()
      handleSelect(allItems[activeIndex])
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  const groupedResults: Array<{ type: SearchResultItem['entityType']; items: SearchResultItem[] }> = []
  if (results) {
    if (results.orders.length > 0) groupedResults.push({ type: 'order', items: results.orders })
    if (results.products.length > 0) groupedResults.push({ type: 'product', items: results.products })
    if (results.customers.length > 0) groupedResults.push({ type: 'customer', items: results.customers })
    if (results.quotes.length > 0) groupedResults.push({ type: 'quote', items: results.quotes })
  }

  const hasResults = allItems.length > 0
  let flatIndex = 0

  return (
    <div ref={containerRef} className="relative w-full" role="search">
      <div className="relative">
        <svg
          viewBox="0 0 20 20"
          fill="currentColor"
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
          aria-hidden="true"
          style={{ color: 'var(--color-text-muted)' }}
        >
          <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
        </svg>
        <input
          ref={inputRef}
          type="search"
          placeholder="Search orders, products..."
          className="w-full rounded-lg py-2 pl-9 pr-3 text-sm outline-none"
          style={{
            backgroundColor: 'var(--color-surface)',
            color: 'var(--color-text)',
            border: '1px solid var(--color-border)',
          }}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => { if (results && allItems.length > 0) setOpen(true) }}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          spellCheck={false}
          aria-label="Global search"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-activedescendant={activeIndex >= 0 ? `search-result-${activeIndex}` : undefined}
        />
        {loading && (
          <span
            className="absolute right-3 top-1/2 h-3 w-3 -translate-y-1/2 animate-spin rounded-full border-2 border-t-transparent"
            style={{ borderColor: 'var(--color-primary) transparent var(--color-primary) var(--color-primary)' }}
            aria-hidden="true"
          />
        )}
      </div>

      {open && (
        <div
          role="listbox"
          aria-label="Search results"
          className="absolute left-0 right-0 z-50 mt-1 overflow-hidden rounded-xl shadow-2xl"
          style={{ backgroundColor: 'var(--color-background)', border: '1px solid var(--color-border)' }}
        >
          {!hasResults ? (
            <div className="px-4 py-3 text-sm" style={{ color: 'var(--color-text-muted)' }}>
              No results for &ldquo;{query}&rdquo;
            </div>
          ) : (
            <div className="max-h-80 overflow-y-auto">
              {groupedResults.map((group) => (
                <div key={group.type}>
                  <div className="px-3 pt-3 pb-1">
                    <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--color-text-muted)', letterSpacing: '0.08em' }}>
                      {entityTypeLabel[group.type]}s
                    </p>
                  </div>
                  {group.items.map((item) => {
                    const idx = flatIndex++
                    return (
                      <button
                        key={item.id}
                        id={`search-result-${idx}`}
                        role="option"
                        aria-selected={activeIndex === idx}
                        type="button"
                        className="flex w-full items-center gap-3 px-3 py-2 text-left"
                        style={{ backgroundColor: activeIndex === idx ? 'var(--color-surface)' : 'transparent' }}
                        onMouseEnter={() => setActiveIndex(idx)}
                        onClick={() => handleSelect(item)}
                      >
                        <span
                          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
                          style={{ backgroundColor: entityTypeColor[item.entityType] }}
                          aria-hidden="true"
                        >
                          {item.entityType[0].toUpperCase()}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium" style={{ color: 'var(--color-text)' }}>{item.label}</p>
                          <p className="truncate text-xs" style={{ color: 'var(--color-text-muted)' }}>{item.sublabel}</p>
                        </div>
                      </button>
                    )
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
